import {
  ShieldCheck,
  FileStack,
  Users,
  Lock,
  Bell,
  ClipboardList,
  UserCircle,
  SlidersHorizontal,
  Building2,
  LayoutDashboard,
  MonitorSmartphone,
  Trash2,
} from "lucide-react";
import type { ComponentType } from "react";

export type AdministrationNavItem = {
  /** Stable id. Present when the item follows a feature switch. */
  id?: string;
  /** GET /features key. Omitted items are not switchable. */
  featureKey?: string;
  label: string;
  path: string;
  icon: ComponentType<{ className?: string }>;
  /** Optional query string (e.g. ?tab=exceptions). */
  search?: string;
  /** Show PENDING governance exception count badge (admin only). */
  pendingExceptionsBadge?: boolean;
  /** Opens workspaces browser modal (?workspaces=1) instead of navigating to `path`. */
  opensWorkspacesModal?: boolean;
  /** Match these pathnames as active in addition to `path`. */
  alsoMatchPaths?: string[];
};

export type AdministrationNavGroup = {
  label: string;
  items: AdministrationNavItem[];
  /** When true, only platform admins see this group. */
  adminOnly?: boolean;
};

/**
 * ADMIN-NAV-1 (FR-2026-10-15-18, TR-2026-10-15-32) — Settings & Administration nav.
 * MY ACCOUNT for everyone; admin groups for Organization Admins only.
 * Appearance lives under Preferences. ONE-TRUTH-FE-2 (ruling on 6332470911): no "Coming
 * soon" entries — Capacity and Workload heatmap are post-MVP and not in the panel
 * (supersedes FR-2026-10-15-20's Resources group).
 * Not in the panel, routes shut: Import/Export, Integrations, Billing. Branding lives in General.
 */
const ADMINISTRATION_NAV_GROUPS_ALL: AdministrationNavGroup[] = [
  {
    label: "My Account",
    adminOnly: false,
    items: [
      {
        label: "Profile",
        path: "/settings/profile",
        icon: UserCircle,
        alsoMatchPaths: ["/administration/profile"],
      },
      {
        label: "Preferences",
        path: "/settings/preferences",
        icon: SlidersHorizontal,
        alsoMatchPaths: ["/administration/preferences"],
      },
      {
        label: "Notifications",
        path: "/settings/notifications",
        icon: Bell,
        alsoMatchPaths: ["/administration/notifications"],
      },
      { label: "My sessions", path: "/settings/security", icon: MonitorSmartphone },
    ],
  },
  {
    label: "Organization",
    adminOnly: true,
    items: [
      { label: "Overview", path: "/administration/overview", icon: LayoutDashboard },
      { label: "General", path: "/administration/general", icon: Building2 },
      {
        label: "People & Teams",
        path: "/administration/people",
        icon: Users,
        alsoMatchPaths: ["/administration/teams"],
      },
      {
        label: "Security & access",
        path: "/administration/security",
        icon: Lock,
      },
    ],
  },
  {
    label: "Governance",
    adminOnly: true,
    items: [
      {
        id: "governance-policies",
        featureKey: "governance-rule-engine",
        label: "Governance",
        path: "/administration/governance",
        icon: ShieldCheck,
      },
      { label: "Templates", path: "/administration/templates", icon: FileStack },
      { label: "Audit trail", path: "/administration/audit-trail", icon: ClipboardList },
    ],
  },
  {
    label: "Data",
    adminOnly: true,
    items: [{ label: "Trash", path: "/administration/trash", icon: Trash2 }],
  },
];

export const ADMINISTRATION_NAV_GROUPS: AdministrationNavGroup[] = ADMINISTRATION_NAV_GROUPS_ALL;

/** Flat list of all nav items — used by layout for rendering and by tests. */
export const ADMINISTRATION_NAV_ITEMS: AdministrationNavItem[] =
  ADMINISTRATION_NAV_GROUPS.flatMap((group) => group.items);
